import React, { useState, useEffect } from 'react';
import { Icon } from '@iconify/react/dist/iconify.js';
import { useClientsData } from '../../contexts/ClientsDataContext';

// Helper function to highlight search text
const highlightText = (text, searchTerm) => {
  if (!searchTerm || !text) return text;
  
  const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
  const parts = text.split(regex);
  
  return parts.map((part, index) => 
    regex.test(part) ? (
      <span key={index} className="bg-yellow-200 text-yellow-900 px-1 rounded">
        {part}
      </span>
    ) : part
  );
};

const AdminClientsTab = ({ searchQuery }) => {
  const [expandedClient, setExpandedClient] = useState(null);

  // Use the ClientsDataContext for real data
  const { 
    organizations, 
    orgMembers, 
    isLoading: isDataLoading, 
    error: dataError 
  } = useClientsData();

  // Filter clients based on search query
  const filteredClients = organizations ? organizations.filter(org => {
    if (!searchQuery) return true;
    
    const searchLower = searchQuery.toLowerCase();
    const searchableFields = [
      org.name,
      org.display_name,
      org.metadata?.customer_id,
      org.metadata?.client_id
    ].filter(Boolean); // Remove undefined values
    
    return searchableFields.some(field => 
      field.toLowerCase().includes(searchLower)
    );
  }) : [];
  
  // Update the count in the parent component
  useEffect(() => {
    if (window.updateAdminCount) {
      window.updateAdminCount('clientsCount', filteredClients.length);
    }
  }, [filteredClients.length, searchQuery]);

  // Always update count when component mounts or search changes, regardless of visibility
  useEffect(() => {
    if (window.updateAdminCount) {
      window.updateAdminCount('clientsCount', filteredClients.length);
    }
  }, [filteredClients.length, searchQuery]);

  const toggleClientExpand = (clientId) => {
    if (expandedClient === clientId) {
      setExpandedClient(null);
    } else {
      setExpandedClient(clientId);
    }
  };
  
  // Show loading state while data is being fetched
  if (isDataLoading) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Icon icon="eos-icons:loading" className="text-4xl text-primary animate-spin" />
          <p className="text-font-secondary">Loading clients data...</p>
        </div>
      </div>
    );
  }

  // Show error state if data fetching failed
  if (dataError) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <div className="flex flex-col items-center gap-4 p-6 bg-red-900 bg-opacity-20 rounded-lg border border-red-500">
          <Icon icon="mdi:alert-circle" className="text-4xl text-error" />
          <p className="text-red-300 text-center">Failed to load clients data</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col md:flex-row flex-1 gap-4 w-full min-h-0">
      {/* Left Side - Client List (Single Column) */}        
      {/* Client List - Scrollable */}
      <div className="flex flex-col gap-2 w-full md:w-1/2 scrollable">
        {filteredClients.map((org) => (
          <>
            <div
            key={org.id}
            className={`
              flex flex-col rounded-lg transition-all duration-200 hover:scale-[1.01] cursor-pointer
              ${expandedClient === org.id
                ? ''
                : ''
              }
            `}
            onClick={() => toggleClientExpand(org.id)}
            >
              {/* Client Card - Compact */}
              <div className={`flex flex-row items-center gap-2 px-3 py-2 transition-all duration-200 ${expandedClient === org.id ? 'tag-secondary' : 'card-container hover:tag-secondary'}`}>
                <Icon
                  icon={org.name === 'helios' ? "mdi:administrator-outline" : "octicon:organization-16"}
                  className={`text-2xl ${org.name === 'helios' ? 'text-primary' : 'text-accent'}`}
                />
                <div className="flex flex-col flex-1 min-w-0">
                  <p className="font-semibold text-base truncate">{org.display_name || org.name}</p>
                  <p className="text-sm text-font-secondary">{orgMembers[org.id]?.length || 0} members</p>
                </div>
                <div className="flex items-center gap-2">
                  <Icon
                    icon={expandedClient === org.id ? "icon-park-solid:left-c" : "icon-park-outline:right-c"}
                    className={`text-lg ${org.name === 'helios' ? 'text-primary' : 'text-accent'}`}
                  />
                </div>
              </div>
            </div>
          </>
        ))}

        {filteredClients.length === 0 && (
          <div className="text-center py-10 text-font-secondary">No clients found.</div>
        )}
      </div>
      {/* Right Side - Client Details (Half Width) */}
      <div className={`details-container w-full md:w-1/2 flex flex-col p-4 ${expandedClient ? '' : 'bg-bgSecondary card-container-inner'}`}>
        {expandedClient ? (
          <>
             {/* Content - Scrollable */}
             <div className="flex-1 h-full">
               {(() => {
                 const org = filteredClients.find(o => o.id === expandedClient);
                 if (!org) return null;

                return (
                  <div className="flex flex-col gap-4">
                    {/* Logo Section */}
                    <div className="flex items-center justify-center p-6 bg-gray-800 bg-opacity-20 rounded-lg">
                      {org.branding?.logo_url ? (
                        <img
                          src={org.branding.logo_url}
                          alt={org.display_name || org.name}
                          className="max-w-full max-h-[120px] object-contain"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div className="flex items-center justify-center w-full h-full" style={{display: org.branding?.logo_url ? 'none' : 'flex'}}>
                        <Icon
                          icon={org.name === 'helios' ? "mdi:shield-crown" : "mdi:office-building"}
                          className={`text-6xl ${org.name === 'helios' ? 'text-primary' : 'text-accent'} opacity-50`}
                        />
                      </div>
                    </div>

                    {/* Info Section */}
                     <div className="flex flex-col gap-3">
                       <div className="flex flex-row items-center justify-between w-full gap-2">
                         <p className="text-xs text-font-secondary uppercase tracking-wide">Organization ID</p>
                         <p className="text-sm p-2 bg-bgSecondary card-container-inner rounded font-mono">
                           {org.name}
                         </p>
                       </div>
                       <hr className="border-bgAccent" />
                       <div className="flex flex-row items-center justify-between w-full gap-2">
                         <p className="text-xs text-font-secondary uppercase tracking-wide">Default Role</p>
                         <p className="text-sm p-2 bg-bgSecondary card-container-inner rounded">
                           {org.name === 'helios' ? 'helios_admin' : 'client_admin'}
                         </p>
                       </div>
                       <hr className="border-bgAccent" />
                       {org.metadata?.client_id && (
                         <div className="flex flex-row items-center justify-between w-full gap-2">
                           <p className="text-xs text-font-secondary uppercase tracking-wide">Client ID</p>
                           <p className="text-sm p-2 bg-bgSecondary card-container-inner rounded font-mono">
                             {org.metadata.client_id}
                           </p>
                         </div>
                       )}
                     </div>

                    {/* Members Section */}
                     <div className="flex flex-col gap-3">
                       <div className="text-xl flex items-center gap-3 pb-3 border-b border-border">
                         <Icon icon="mdi:account-group" className="text-3xl text-primary" />
                         <div>
                           <h3 className="font-semibold">Organization Members</h3>
                           <p className="text-sm text-font-secondary">{orgMembers[org.id]?.length || 0} total members</p>
                         </div>
                       </div>
                      <div className="flex flex-col gap-2 sm:gap-3">
                        {orgMembers[org.id]?.length > 0 ? (
                          orgMembers[org.id].map((member, idx) => (
                            <div 
                              key={member.user_id} 
                              className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-3 p-2 sm:p-3 bg-bgSecondary card-container rounded-md sm:rounded-lg hover:bg-opacity-80 transition-colors"
                            >
                              <div className="flex flex-row gap-2 sm:gap-3 items-center flex-1 min-w-0">
                                <img
                                  src={member.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(member.name || member.email)}&background=00aeef&color=fff`}
                                  alt={member.name}
                                  className="w-10 h-10 sm:w-12 sm:h-12 rounded-md flex-shrink-0"
                                  onError={(e) => {
                                    e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(member.name || member.email)}&background=00aeef&color=fff`;
                                  }}
                                />
                                <div className="flex flex-col min-w-0 flex-1 gap-1">
                                  <span className="font-medium truncate text-font-primary text-sm sm:text-base">
                                    {member.name || 'No name'}
                                  </span>
                                  <span className="text-xs sm:text-sm text-font-secondary truncate">
                                    {member.email}
                                  </span>
                                </div>
                              </div>

                              <div className="flex flex-row items-center justify-between gap-2 sm:gap-3 w-full sm:w-auto sm:flex-shrink-0">
                                <div className="flex-shrink-0">
                                  {member.roles && member.roles.length > 0 && (
                                    <span className="text-xs sm:text-xs text-accent bg-bgSecondary card-container px-2 py-1 rounded">
                                      {member.roles[0].name}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="flex flex-col items-center justify-center py-6 sm:py-8 text-font-secondary">
                            <Icon icon="mdi:account-off" className="text-3xl sm:text-4xl mb-2" />
                            <p className="italic text-sm sm:text-base">No members found</p>
                          </div>
                        )}
                      </div>
                     </div>
                  </div>
                );
              })()}
            </div>
          </>
        ) : (
          /* No Client Selected State */
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center text-gray-400">
              <Icon icon="mdi:hand-pointing-left" className="text-6xl mx-auto mb-4 opacity-50" />
              <p className="text-lg">Select a client to view details</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminClientsTab;
